import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useAxios } from "hooks/useAxios";
import { USER_API } from "config";
import { sessionKeys } from "queries/profile.keys";
import type { AppState } from "store/reducers/Reducer";
import type { SessionUser } from "types/session-user";
import { handleToast } from "utils/toasts";
import { fetchSessionUser } from "./fetchSessionUser";
import { sessionQueryRetry } from "./sessionQueryRetry";
import { toSessionUser } from "./toSessionUser";
import type { ProfileType } from "store/types/profile";

export type SessionUserUpdatePayload = Partial<{
  first_name: string;
  last_name: string;
  profile_image_path: string | null;
  marketing_opt_in: boolean;
}>;

export const useSessionUser = () => {
  const axios = useAxios({ allResponse: true });
  const queryClient = useQueryClient();
  const token = useSelector((state: AppState) => state.auth.token);
  const cachedSession = queryClient.getQueryData<SessionUser>(sessionKeys.me());

  const query = useQuery<SessionUser>({
    queryKey: sessionKeys.me(),
    queryFn: () => fetchSessionUser(axios),
    enabled: Boolean(cachedSession) || Boolean(token),
    staleTime: 5 * 60 * 1000,
    retry: sessionQueryRetry,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: SessionUserUpdatePayload) => {
      const response = await axios.patch(USER_API.me, payload);
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Failed to update profile. Please try again.";
      handleToast(message, "error");
    },
  });

  const update = useCallback(
    (payload: SessionUserUpdatePayload) => updateMutation.mutate(payload),
    [updateMutation]
  );

  const updateAsync = useCallback(
    (payload: SessionUserUpdatePayload) => updateMutation.mutateAsync(payload),
    [updateMutation]
  );

  const patchSessionCache = useCallback(
    (updater: (session: SessionUser) => SessionUser) => {
      queryClient.setQueryData<SessionUser>(sessionKeys.me(), (session) =>
        session ? updater(session) : session
      );
    },
    [queryClient]
  );

  /** Compatibility: login payloads / partials that still look like ProfileType. */
  const seedFromLegacyUser = useCallback(
    (user: ProfileType) => {
      queryClient.setQueryData(sessionKeys.me(), toSessionUser(user));
    },
    [queryClient]
  );

  return {
    ...query,
    session: query.data ?? null,
    /** @deprecated Prefer session.billing_model === "one_time" or session.is_one_time_purchase */
    isOneTimePurchase: Boolean(
      query.data?.billing_model === "one_time" || query.data?.is_one_time_purchase
    ),
    update,
    updateAsync,
    isUpdating: updateMutation.isPending,
    patchSessionCache,
    seedFromLegacyUser,
  };
};
